import * as dayjs from 'dayjs';
import { Injectable } from '@angular/core';
import {SettingsService} from '@app/core/services/settings.service';
import {NGXLogger} from 'ngx-logger';
import {Observable} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import { AuthService } from '../auth/auth.service';
import { Language } from '../constants/language';
import * as localeData from 'dayjs/plugin/localeData';
import 'dayjs/locale/fr';

@Injectable({
  providedIn: 'root'
})
export class ScgSharedFactoryService {


  constructor(private auth: AuthService,
              private translate: TranslateService,
              private settings: SettingsService,
              private log: NGXLogger) { }

  public myFormat(azDate: string) {
    return azDate + '!!!';
  }


  public azDateTimeToDate(azDate: string) {
    const date = dayjs(azDate);
    return date.format('DD.MM.YY');
  }

  public formatAzDate(azDate : string) {
    const date = dayjs(azDate);
    return date.format('DD.MM.YY');
  }

  public isDontShow(id: number): Observable<boolean> {
    return this.settings.getCbHint(id);
  }

  public setDontShow(id: number): void {
    this.settings.setCbHint(id).subscribe(() => {});
  }

  public resetDontShow(id: number): void {
    this.settings.resetCbHint(id).subscribe(() => {});
  }

  public getLocalizedDateString(input): string {
    if (dayjs(input).isValid() === false) {
      return input;
    }
    const apiDate = dayjs(input);
    if (this.isGerman()) {
      return dayjs(apiDate).format('DD.MM.YY');
    }else {
      return dayjs(apiDate).format('DD. MMM YYYY');
    }
  }

  public azDateTimeToDateTime(azDate: string): Date {
    const yyyy = parseInt(azDate.substr(0, 4), 10);
    const MM = parseInt(azDate.substr(5, 2), 10);
    const dd = parseInt(azDate.substr(8, 2), 10);
    const HH = parseInt(azDate.substr(11, 2), 10);
    const mm = parseInt(azDate.substr(14, 2), 10);
    const ss = parseInt(azDate.substr(17, 2), 10);
    return new Date(yyyy, MM, dd, HH, mm, ss, 0);
  }

  public azDateTimeToDateTimeStr(azDate: string): string {
    const yyyy = azDate.substr(0, 4);
    const MM = azDate.substr(5, 2);
    const dd = azDate.substr(8, 2);
    const HH = azDate.substr(11, 2);
    const mm = azDate.substr(14, 2);
    const ss = azDate.substr(17, 2);

    if (this.auth.getCurrentLanguage() === Language.English) {
      return yyyy + '/' + MM + '/' + dd + ' ' + HH + ':' + mm + ':' + ss;
    } else {
      // return DE format by default
      return dd + '.' + MM + '.' + yyyy + ' ' + HH + ':' + mm + ':' + ss;
    }
  }

  public dateDiffToNow(date: Date): number {
    const nowDate = new Date();
    const diff = Math.abs(nowDate.getTime() - date.getTime());
    const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
    return diffDays;
  }

  public isGerman(): boolean {
    return this.auth.getCurrentLanguage() === Language.German;
  }

  public isEnglish(): boolean {
    return this.auth.getCurrentLanguage() === Language.English;
  }

  public isFrench(): boolean {
    return this.auth.getCurrentLanguage() === Language.French;
  }


  public isUS(): boolean {
    if (this.auth.getCurrentLanguage() !== Language.English) return false;    // important for some html markups (e.h. historyChart)
    return (window.navigator?.language === 'en-US');
  }

  public hourToSec(s: string): number {
    const posi = s.indexOf(':');
    if (posi === -1) {
      return 0;
    }
    const hoursStr = String(s.substring(0, posi));
    const minutesStr = String(s.substring(posi + 1, s.length));
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10 );
    const totalsecs = ((hours * 60) + minutes) * 60;
    return totalsecs;
  }

  public hourToMin(s: string): number {
    return this.hourToSec(s) / 60;
  }

  public minToHour(num: number): string {
    const hours = (num / 60);
    const rhours = Math.floor(hours);
    const minutes = (hours - rhours) * 60;
    const rminutes = Math.round(minutes);
    let sminutes = rminutes.toString();
    if (sminutes.length === 1) {
      sminutes = '0' + sminutes;
    }
    let shours = rhours.toString();
    if (shours.length === 1) {
      shours = '0' + shours;
    }
    return shours + ':' + sminutes;
  }

  public formatSecsDash(secs, shortDisplay) {
    if (secs <= 0) return '--:--';
    return this.formatSecs(secs, shortDisplay);
  }


  public formatSecs(secs, shortDisplay) {

    if (isNaN(secs)) {
      return '---';
    }
    if (secs === 0 || secs === -1) {
      if (shortDisplay) {
        return '0:00';
      }
      return '0 min';
    }
    if (secs < 60) {
      if (shortDisplay) {
        return '0:00';
      }
      return '<1 min';
    }
    const hours = Math.floor(secs / 3600);
    secs %= 3600;
    const minutes = Math.floor(secs / 60);
    let minString = 'Minuten';    // default plural
    if (!this.isGerman()) minString = 'minutes';

    if (minutes === 1) {
      minString = 'min';
    }
    let minStr = ''; // MinStr only used for shortDisplay
    if (minutes < 10) {
      minStr = '0' + minutes;
    } else {
      minStr = minutes.toString();
    }
    if (hours > 0) {
      if (minutes > 0) {
        if (shortDisplay) {
          return hours + ':' + minStr;
        }
        if (shortDisplay) {
          return '1:00';
        }
        return hours + ' h ' + minutes + ' min';
      } else {
        if (shortDisplay) {
          return '1:00';
        }
        return hours + ' h ';
      }
    } else {
      if (shortDisplay) {
        return '0:' + minStr;
      }
      return minutes + ' ' + minString;
    }
  }

  public formatDateTimeXS(azDateTime: string|dayjs.Dayjs) {
    if (azDateTime == null) {
      return '???';
    }

    let formatStr = 'DD.MM.  HH:mm'; // DE default
    if (this.auth.getCurrentLanguage() !== Language.German) {
      formatStr = 'DD/MM  HH:mm';   // default for en and fr
      if (this.auth.getCurrentLanguage() === Language.English && this.isUS()) formatStr = 'MM/DD  hh:mm A';    // usa special
    }
    return dayjs(azDateTime).format(formatStr);



  }

  public formatWeekdayDate(apidate) {

    if (apidate && apidate.length !== 19) {
      return '';
    } else {
      const tmpDate = new Date(apidate);
      const tmpDay = tmpDate.getDay();
      /*if (isNaN(tmpDay)===true) return '';
      const weekday = '';
      if (tmpDay===0) tmpDay
      if (tmpDay===1) {tmpDay=$translate.instant('TIMES.SHORT_MON');}
      if (tmpDay===2) {tmpDay=$translate.instant('TIMES.SHORT_TUE');}
      if (tmpDay===3) {tmpDay=$translate.instant('TIMES.SHORT_WED');}
      if (tmpDay===4) {tmpDay=$translate.instant('TIMES.SHORT_THU');}
      if (tmpDay===5) {tmpDay=$translate.instant('TIMES.SHORT_FRI');}
      if (tmpDay===6) {tmpDay=$translate.instant('TIMES.SHORT_SAT');}
      if (tmpDate.getDay()>=0) {tmpDay=tmpDay+", ";}
      return tmpDay;*/
    }
  }

  public formatDateTime(azDateTime) {
    if (azDateTime == null) {
      return '???';
    }
    let formatStr = 'DD.MM.YY  HH:mm:ss'; // DE default
    if (this.auth.getCurrentLanguage() !== Language.German) {
      formatStr = 'DD/MM/YYYY  HH:mm:ss';   // default for en and fr
      if (this.auth.getCurrentLanguage() === Language.English && this.isUS()) formatStr = 'MM/DD/YY  hh:mm A';    // usa special
    }
    return dayjs(azDateTime).format(formatStr);
  }

  public isValidEmail(email): boolean {
    email = email.toLowerCase();
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  }

  public isMobile() {
    if (navigator.userAgent.match(/Android/i)
      || navigator.userAgent.match(/webOS/i)
      || navigator.userAgent.match(/iPhone/i)
      || navigator.userAgent.match(/iPad/i)
      || navigator.userAgent.match(/iPod/i)
      || navigator.userAgent.match(/BlackBerry/i)
      || navigator.userAgent.match(/Windows Phone/i)
    ){
      return true;
    }
    else {
      return false;
    }
  }

  public gaEvent(action: string) {
    // no more working because of change to GA4, TODO 2023: Implement GA4
    // this.gaService.event(action, 'Portal-V2');
  }


  public formatHM(period: string): string {
    try {
      const splitty = period.split(':', 2);
      splitty[0] = splitty[0].replace('00', '');
      splitty[1].replace('00', '');
      let retval = '';
      if (splitty[0].substring(0, 1) === '0') splitty[0] = splitty[0].substring(1, 2);
      if (splitty[0] !== '') retval = retval + splitty[0] + ' h ';
      if (splitty[1].substring(0, 1) === '0') splitty[1] = splitty[1].substring(1, 2);
      if (splitty[1] !== '') retval = retval + splitty[1] + ' min';
      return retval;
    }
    catch (err) {
      return period;
    }
  }

  public formatHMForSecs(seconds: number): string {
    const secs = seconds.toString();
    const x = this.formatSecs(seconds, true);
    return this.formatHM(x);
  }

  public getTLD(url: string): string {
    let retval = url;
    if (!retval) return retval;
    if (retval.indexOf('.') === -1) return retval; // in case of app (bergfex/ski)
    const posi = retval.indexOf('/');
    if (posi > 0 ) {
      retval = retval.substring(0, posi);
    }
    return retval;
  }


}

export enum Hint {
  DevHint = 42,
  DevHint2 = 43
}

export enum DeviceWarningType {
  DeviceAdmin = 1,
  Accessibility = 2,
  Overlay = 3,
  PlayStoreIssue = 4
}


